import CreateIcon from "@mui/icons-material/Add";
import DeleteIcon from "@Assets/icons/svg/delete.svg?react";
import CancelIcon from "@Assets/icons/svg/Cancel.svg?react";
import EditIcon from "@Assets/icons/edit.svg?react";
import UpdateIcon from "@Assets/icons/svg/Update.svg?react";
import SlickGrid1, { GridRowBase, SlickGrid1Ref, typedColumns } from "@CubeComponents/html/SlickGrid1";
import { useCallback, useMemo, useRef, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import NumericBoxComponent from "@CubeComponents/form/NumericBoxComponent";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "@i18n";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import Text from "@CubeComponents/html/Text/Text";
import IconComponent from "@CubeComponents/icons";
import { EditorTemplateArgs, ToolbarItem } from "@WebTypes/scheduleDomain";
import { FileManagerAsPopup } from "@Features/files";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";

const MAX_TASK_FILES = 100;

export interface TaskFileRow extends GridRowBase {
  id: string;
  fileVersionId: string;
  name: string;
  weightage: number | null;
  version: number;
}

export interface ActivityFilesProps {
  spaceId?: string;
  projectId?: string;
  fileAddEditData: TaskFileRow[];
  setFileAddEditData: (value: TaskFileRow[]) => void;
  setFilesChanged: (value: boolean) => void;
}

const FILE_RESOURCE_TYPES = ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"];

/**
 * SlickDataView declares `getItem()` as returning the row, but it hands back `undefined` for a row
 * index past the end of its current data — a selected index can outlive the row it pointed at.
 * Reading through this function keeps the caller's guard meaningful: assigning the call straight
 * into a `TaskFileRow | undefined` const does not help, because control-flow analysis re-narrows a
 * const to its initialiser's type.
 */
const dataViewRowAt = (grid: SlickGrid1Ref<TaskFileRow>, rowIndex: number): TaskFileRow | undefined =>
  grid.dataView.getItem(rowIndex);

function ActivityFiles(props: ActivityFilesProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { fileAddEditData, setFileAddEditData, setFilesChanged } = props;
  const [showFilePopup, setShowFilePopup] = useState(false);
  const [fileEditMode, setFileEditMode] = useState(false);
  const [disableDelete, setDisableDelete] = useState(true);
  const [disableEdit, setDisableEdit] = useState(true);
  const weightageEditsRef = useRef<Record<string, number | null>>({});
  const slickGridRef = useRef<SlickGrid1Ref<TaskFileRow> | null>(null);

  const slickGridOptions = useMemo(
    () => ({
      forceFitColumns: true,
      enableGridMenu: false,
      enableFiltering: false,
      enableCellMenu: false,
      enableHeaderMenu: false,
    }),
    [],
  );

  const weightageEditorTemplate = useCallback((args: EditorTemplateArgs<TaskFileRow>) => {
    const row = args.args;
    const initialValue = row.id in weightageEditsRef.current ? weightageEditsRef.current[row.id] : row.weightage;
    return (
      <NumericBoxComponent
        className="w-full"
        width="100%"
        defaultValue={initialValue ?? 0}
        min={0}
        placeholder={t("Select Value")}
        onChange={(event) => {
          weightageEditsRef.current[row.id] = event.target.value;
        }}
      />
    );
  }, []);

  const fileGridColumns = useMemo(
    () =>
      typedColumns<TaskFileRow>([
        {
          field: "name",
          id: "name",
          width: 60,
          textAlign: "Left",
          sortable: true,
          name: t("Name"),
        },
        {
          field: "weightage",
          id: "weightage",
          width: 25,
          textAlign: "Left",
          sortable: false,
          name: t("Weightage"),
          editorTemplate: weightageEditorTemplate,
        },
        {
          field: "version",
          id: "version",
          width: 15,
          textAlign: "Left",
          sortable: true,
          name: t("Version"),
          formatter: (_row, _cell, value) => (value || value === 0 ? `V${value}` : ""),
        },
      ]),
    [weightageEditorTemplate],
  );

  const handleRowSelection = () => {
    if (slickGridRef.current) {
      const selectedRows = slickGridRef.current.slickGrid.getSelectedRows();
      setDisableDelete(selectedRows.length < 1);
      setDisableEdit(selectedRows.length !== 1);
    }
  };

  const setEmptySelectedRows = () => {
    if (slickGridRef.current) {
      slickGridRef.current.slickGrid.setSelectedRows([]);
      slickGridRef.current.dataView.setSelectedIds([]);
    }
  };

  const handleFileSelected = (data: Record<string, SelectedFileItem>) => {
    const existing = new Set(fileAddEditData.map((row) => row.fileVersionId));
    const newRows: TaskFileRow[] = [];
    // The popup always passes its selection map (state initialised to `{}`), so iterate the values
    // directly — indexing by key would only reintroduce an `undefined` the map cannot produce.
    Object.values(data).forEach((file) => {
      if (!(FILE_RESOURCE_TYPES as (string | undefined)[]).includes(file.resourceType)) return;
      const fileVersionId = file.fileVersion;
      if (!fileVersionId || existing.has(String(fileVersionId))) return;
      existing.add(String(fileVersionId));
      newRows.push({
        id: uuidv4(),
        fileVersionId: String(fileVersionId),
        name: file.name ?? "",
        weightage: null,
        // KNOWN BUG, preserved verbatim: `versionNumber` is optional on a file-manager selection, so a
        // folder-ish or version-less node lands here as `undefined` and renders as "V". Erased
        // assertion; defaulting it would change behaviour.
        version: file.versionNumber!,
      });
    });
    if (newRows.length) {
      if (fileAddEditData.length + newRows.length > MAX_TASK_FILES) {
        dispatch(
          showToastNotification({
            content: t("You can attach up to {{count}} files to an activity.", { count: MAX_TASK_FILES }),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }
      setFileAddEditData([...fileAddEditData, ...newRows]);
      setFilesChanged(true);
    }
    setShowFilePopup(false);
  };

  const handleDeleteFiles = () => {
    const grid = slickGridRef.current;
    if (!grid) return;
    const selectedRows: number[] = grid.slickGrid.getSelectedRows();
    const ids = new Set(selectedRows.map((i) => dataViewRowAt(grid, i)?.id));
    const updatedItems = fileAddEditData.filter((row) => !ids.has(row.id));
    setFileAddEditData(updatedItems);
    setFilesChanged(true);
    setEmptySelectedRows();
    setDisableDelete(true);
    setDisableEdit(true);
  };

  const handleEnterEditMode = () => {
    // Keep the current selection: SlickGrid uses the selected row as the editing row.
    weightageEditsRef.current = {};
    setFileEditMode(true);
  };

  const handleUpdateFiles = () => {
    const edits = weightageEditsRef.current;
    const updatedItems = fileAddEditData.map((row) =>
      row.id in edits ? { ...row, weightage: edits[row.id] ?? null } : row,
    );
    setFileAddEditData(updatedItems);
    setFilesChanged(true);
    setFileEditMode(false);
    weightageEditsRef.current = {};
    setEmptySelectedRows();
    setDisableDelete(true);
    setDisableEdit(true);
  };

  const handleCancelEdit = () => {
    setFileEditMode(false);
    weightageEditsRef.current = {};
    setEmptySelectedRows();
    setDisableDelete(true);
    setDisableEdit(true);
  };

  const toolbar: ToolbarItem[] = useMemo(
    () => [
      {
        name: t("Add"),
        id: "add",
        prefixIconTemplate: <CreateIcon />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
        disabled: fileEditMode,
        hidden: fileEditMode,
      },
      {
        name: t("Edit"),
        id: "edit",
        prefixIconTemplate: <EditIcon className="w-4" />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: disableEdit,
        hidden: fileEditMode,
      },
      {
        name: t("Update"),
        id: "update",
        prefixIconTemplate: <UpdateIcon className="w-4" />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        hidden: !fileEditMode,
      },
      {
        name: t("Remove"),
        id: "delete",
        prefixIconTemplate: <DeleteIcon className="w-4" />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: disableDelete,
        hidden: fileEditMode,
      },
      {
        name: t("Cancel"),
        id: "cancel",
        prefixIconTemplate: <CancelIcon className="w-4" />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        hidden: !fileEditMode,
      },
    ],
    [fileEditMode, disableDelete, disableEdit],
  );

  const toolbarClickHandler = (cmd: string) => {
    if (cmd === "add") {
      setShowFilePopup(true);
    } else if (cmd === "edit") {
      handleEnterEditMode();
    } else if (cmd === "update") {
      handleUpdateFiles();
    } else if (cmd === "delete") {
      handleDeleteFiles();
    } else if (cmd === "cancel") {
      handleCancelEdit();
    }
  };

  const noFilesIllustration = () => (
    <div className="flex flex-col items-center justify-center w-full h-full overflow-hidden">
      <IconComponent name="NoData" className="flex overflow-hidden w-full h-full max-w-[12.5rem] max-h-[12.5rem]" />
      <Text className="mt-2 text-[#666A84] text-center font-semibold text-base">{t("No Files added")}</Text>
      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        onClick={() => setShowFilePopup(true)}
        startIcon={<IconComponent name="addIconBold" className="w-4" />}
        className="mt-3"
      >
        {t("Add")}
      </ButtonComponent>
    </div>
  );

  return (
    <div className="flex flex-col overflow-auto h-full ps-3 pe-3 pb-3">
      <SlickGrid1
        toolbar={fileAddEditData.length === 0 ? undefined : toolbar}
        toolbarClickHandler={toolbarClickHandler}
        ref={slickGridRef}
        columnDefinitions={fileGridColumns}
        gridOptions={slickGridOptions}
        dataset={fileAddEditData}
        isEditMode={fileEditMode}
        onSelectedRowIdsChanged={handleRowSelection}
        noDataIllustration={noFilesIllustration}
      />
      {showFilePopup && (
        <FileManagerAsPopup
          projectId={props.projectId}
          workspaceId={props.spaceId}
          handleFileSelectedButtonClick={handleFileSelected}
          handleFileSelectedCancelClick={() => setShowFilePopup(false)}
          heading={t("Attach Files")}
          hideEstimates
          hideSchedules
          hideIssues
          hideTasks
          disableFiles={fileAddEditData.map((row) => row.fileVersionId)}
          enableFilesTypes={["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_FOLDER"]}
        />
      )}
    </div>
  );
}

export default ActivityFiles;
